// For v2 of the seismic submittal tool
import axios from 'axios';
import Vue from 'vue';
import { debounce } from 'lodash';

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

export default {
    namespaced: true,

    strict: false,

    state: {
        fields: [
            'structure',
            'udStructure',
            'substrate',
            'udSubstrate',
            'structuralAttachment',
            'fastener',
            'udFastener',
            'fastenerOrientation',
            'fastenerSize',
            'fastenerEmbedment',
            'fastenerEffectiveEmbedment',
            'udEmbedment',
            'udEffectiveEmbedment',
            'udFastenerLoad',
            'loadOrientation',
            'swivel',
            'systemMaterial',
        ],

        optionsAreLoading: false,

        options: {
            structure: [],
            substrate: [],
            fastener: [],
            fastenerOrientation: [],
            fastenerSize: [],
            fastenerEmbedment: [],
            structuralAttachment: [],
            loadOrientation: [],
            swivel: null,
        },

        values: {
            structure: null,
            udStructure: null,
            substrate: null,
            udSubstrate: null,
            fastener: null,
            udFastener: null,
            fastenerOrientation: null,
            fastenerSize: null,
            fastenerEmbedment: null,
            fastenerEffectiveEmbedment: null,
            udEmbedment: null,
            udEffectiveEmbedment: null,
            udFastenerLoad: null,
            structuralAttachment: null,
            loadOrientation: null,
            beamThickness: null,
            angleMin: null,
            angleMax: null,
            lrMax: null,
            braceMember: null,
            agency: null,
            swivel: [],
            systemMaterial: null,
        },

        netVerticalReactionMessage: null,
        braceMembers: [],

        fastenerBraceInfo: {
            fastenerOrientations: [],
            braceMemberCapacity: null,
            braceMemberLength: null,
            leastRadiusOfGyration: null
        },

        structuralAttachmentOptions: [],
        pipeAttachmentOptions: [],

        primaryStructuralAttachment: null,
        primaryPipeAttachment: null,

        menuOptions: {
            structures: [],
            substrates: [],
            fasteners: [],
            fastenerOrientations: [],
            fastenerSizes: [],
            fastenerEmbedments: [],
            structuralAttachments: [],
            loadOrientations: [],
            swivels: [],
        }
    },
    getters: {
        getMenuOptions(state){
            return state.menuOptions;
        }
    },
    actions: {
        sample: debounce(({commit, getters, rootGetters}, payload = {}) => {
            let url = `/actions/seismic-submittal/pipe/get-max-fpw-load`;

            let sgmts = getters.mainBranchSegments.map(s => {
                    if (!s.pipeName || !s.pipeSize || !s.pipeType) {
                        return false;
                    }
                    return { pipeName: s.pipeName, pipeSize: s.pipeSize, pipeType: s.pipeType };
                });

            return axios.post(url, {
                sid: rootGetters['submittalStandards/id'],
                length: getters.mainTotalLength,
                segments: sgmts,
            }).then(r => commit('setMaxFpw', r.data))
            .then(function(){ commit('calculateWeights', { rootGetters }) });
        }, 200),
        fetchFormOptions: ({commit, state, rootState, rootGetters}) => {
            let system = rootState.submittalSystem;
            let brace = rootState.submittalBrace.records[system.id];
            let material = (brace && brace.systemMaterial) ? brace.systemMaterial : 'Carbon Steel';

            let params = {
                standardId: rootGetters['submittalStandards/standardId'],
                systemMaterial: material,
                braceType: system.orientation,
                agency: system.design.agency,
            };

            state.optionsAreLoading = true;

            let makeRequest = params.standardId && params.systemMaterial;
  
            if (!makeRequest) {
                return Promise.reject('no standardId or systemMaterial');
            }
            
            return axios.get('/actions/seismic-submittal/attachment/all-menu-options', { params })
                .then((r) => {
                    commit('setMenuOptions', r.data);
                    state.optionsAreLoading = false;
                });
        },
        fetchOptions: ({commit, state, rootGetters, rootState}, payload) => {
            let system = rootState.submittalSystem;
            let brace = rootState.submittalBrace.records[system.id];
            let material = (brace && brace.systemMaterial) ? brace.systemMaterial : 'Carbon Steel';

            let params = {
                standardId: rootGetters['submittalStandards/standardId'],
                systemMaterial: material,
                braceType: system.orientation,
                fpw: system.zoiSeismicDesignLoad,
                pipeSize: rootGetters['submittalSystem/largestMainBranchPipeSizeAndType'].pipeSize,
                ...state.values,
            };

            // Overwrite value from state.agency with system.design.agency
            params.agency = system.design.agency;

            state.optionsAreLoading = true;

            let makeRequest = params.braceType
                && params.standardId
                && params.fpw
                && params.systemMaterial;

            if (!makeRequest) {
                return Promise.reject('invalid params');
            }

            return axios.get('/actions/seismic-submittal/attachment/get-brace-configuration-dropdown-options', { params })
                .then((r) => {
                    commit('mergeOptions', r.data);
                    state.optionsAreLoading = false;
                });
        },

        setValues({commit, state, dispatch}, payload) {
            commit('setValues', payload);
            return dispatch('fetchOptions');
        },

        fetchStructuralAttachmentOptions: ({commit, state, rootGetters, rootState}, payload) => {
            let system = rootState.submittalSystem;
            let brace = rootState.submittalBrace.records[system.id];
            let material = (brace && brace.systemMaterial) ? brace.systemMaterial : 'Carbon Steel';

            let params = {
                standardId: rootGetters['submittalStandards/standardId'],
                systemMaterial: material,
                braceType: system.orientation,
                fpw: system.zoiSeismicDesignLoad,
                pipeSize: rootGetters['submittalSystem/largestMainBranchPipeSizeAndType'].pipeSize,
                ...state.values
            };

            // Overwrite value from state.agency with system.design.agency
            params.agency = system.design.agency;
            
            let makeRequest = (
                params.standardId && 
                params.systemMaterial &&
                params.fpw &&
                params.braceType &&
                params.structure &&
                params.substrate &&
                params.angleMin &&
                params.angleMax &&
                params.lrMax &&
                params.braceMember &&
                params.agency
            );

            if (!makeRequest) {
                return Promise.reject();
            }

            state.optionsAreLoading = true;

            return axios.get('/actions/seismic-submittal/attachment/get-structural-attachment-choices', {params})
                .then(r => {
                    commit('setStructuralAttachmentOptions', r.data);
                state.optionsAreLoading = false;
                });
        },

        fetchPipeAttachmentOptions: ({commit, state, rootGetters, rootState}, payload) => {
            let system = rootState.submittalSystem;
            let brace = rootState.submittalBrace.records[system.id];
            let material = (brace && brace.systemMaterial) ? brace.systemMaterial : 'Carbon Steel';

            let pipe = rootGetters['submittalSystem/largestMainBranchPipeSizeAndType'];

            // Note, the pipe attachments table uses the string version of size (1 NPS) not numeric size
            let params = {
                standardId: rootGetters['submittalStandards/standardId'],
                systemMaterial: material,
                braceType: system.orientation,
                agency: system.design.agency,
                fpw: system.zoiSeismicDesignLoad,
                pipeSize: pipe.pipeName,
                pipeType: pipe.pipeType,
                ...state.values
            };

            // Overwrite value from state.agency with system.design.agency
            params.agency = system.design.agency;

            let makeRequest = (
                params.standardId && 
                params.systemMaterial &&
                params.fpw &&
                params.braceType &&
                params.pipeSize &&
                params.pipeType &&
                params.angleMin &&
                params.angleMax &&
                params.lrMax &&
                params.braceMember &&
                params.agency
            );

            if (!makeRequest) {
                return Promise.reject();
            }

            return axios.get('/actions/seismic-submittal/attachment/get-pipe-attachment-choices', {params})
                .then(r => {
                    commit('setPipeAttachmentOptions', r.data);
                });
        },

        fetchFastenerBraceInfo: ({commit, state, rootGetters, rootState}) => {
            let system = rootState.submittalSystem;
            let brace = rootState.submittalBrace.records[system.id];
            let material = (brace && brace.systemMaterial) ? brace.systemMaterial : 'Carbon Steel';

            let params = {
                standardId: rootGetters['submittalStandards/standardId'],
                systemMaterial: material,
                ...state.values
            };

            // Overwrite value from state.agency with system.design.agency
            params.agency = system.design.agency;

            let makeRequest = (
                params.standardId && 
                params.systemMaterial && 
                params.angleMin &&
                params.angleMax &&
                params.lrMax &&
                params.braceMember
            );
            
            if (!makeRequest) {
                return Promise.reject();
            }

            return axios.get('/actions/seismic-submittal/attachment/get-fastener-brace-info', {params})
                .then(r => {
                    commit('setFastenerBraceInfo', r.data);
                });
        },

        fetchNetVerticalReactionMessage: ({commit, rootState}, payload) => {
            let system = rootState.submittalSystem;
            let brace = rootState.submittalBrace.records[system.id];
            let material = (brace && brace.systemMaterial) ? brace.systemMaterial : 'Carbon Steel';

            let params = {
                standardId: rootState.submittalStandards.standardId,
                systemMaterial: material,
                braceType: rootState.submittalSystem.orientation,
                fpw: rootState.submittalSystem.zoiSeismicDesignLoad,
                wp: rootState.submittalSystem.zoiSystemDesignWeightWp,
                angleMin: payload.angleMin,
            }

            let makeRequest = (
                params.standardId && 
                params.systemMaterial && 
                params.braceType &&
                params.fpw &&
                params.wp &&
                params.angleMin
            );

            if (makeRequest) {
                return axios.get('/actions/seismic-submittal/attachment/get-net-vertical-reaction-message', {params})
                    .then(r => {
                        commit('setNetVerticalReactionMessage', r.data);
                    });
            }
        },

        fetchBraceMembers: ({commit, state, rootState, rootGetters}) => {
            let system = rootState.submittalSystem;
            let brace = rootState.submittalBrace.records[system.id];
            let material = (brace && brace.systemMaterial) ? brace.systemMaterial : 'Carbon Steel';

            let params = {
                standardId: rootGetters['submittalStandards/standardId'],
                systemMaterial: material,
                fpw: rootState.submittalSystem.zoiSeismicDesignLoad,
                angleMin: state.values.angleMin,
                angleMax: state.values.angleMax,
                lrMax: state.values.lrMax,
            };

            let makeRequest = (
                params.standardId && 
                params.systemMaterial && 
                params.fpw &&
                params.angleMin && 
                params.angleMax &&
                params.lrMax
            );

            if (makeRequest) {
                return axios.get('/actions/seismic-submittal/attachment/get-brace-members', {params})
                    .then(r => {
                        commit('setBraceMembers', r.data);
                    });
            }
        },
    },
    mutations: {
        mergeOptions(state, payload) {
            Vue.set(state, 'options', payload);
        },

        setValues(state, payload) {
            Vue.set(state, 'values', payload);
        },

        mergeValues(state, payload) {
            Vue.set(state, 'values', { ...state.values, ...payload });
        },

        setStructuralAttachmentOptions(state, payload) {
            payload.forEach(attachment => {
                attachment.primary = attachment.primary || false;
            });

            Vue.set(state, 'structuralAttachmentOptions', payload);
        },

        setPipeAttachmentOptions(state, payload) {
            payload.forEach(pipe => {
                pipe.primary = pipe.primary || false;
            });

            Vue.set(state, 'pipeAttachmentOptions', payload);
        },

        setNetVerticalReactionMessage(state, payload) {
            Vue.set(state, 'netVerticalReactionMessage', payload);
        },

        setBraceMembers(state, payload) {
            Vue.set(state, 'braceMembers', payload);
        },

        setFastenerBraceInfo(state, payload) {
            Vue.set(state, 'fastenerBraceInfo', payload);
        },

        setMenuOptions(state, payload) {            
            Vue.set(state, 'menuOptions', payload);
        },
    },
};